import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Send, Loader2, Facebook, Instagram } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TikTokIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

export const SOCIAL_LINKS = {
  facebook: 'https://www.facebook.com/people/Frequency-Forest/61590454420774/',
  instagram: 'https://www.instagram.com/frequencyforest.au/',
  tiktok: 'https://www.tiktok.com/@frequencyforest.au',
};

const quickLinks = [
  { name: 'Home', path: '/' },
  { name: 'Lineup', path: '/lineup' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'FAQ & Contact', path: '/faq-contact' },
];

const eventLinks = [
  { name: 'Local Area', path: '/event-details/local-area' },
  { name: 'How To Get There', path: '/event-details/how-to-get-there' },
  { name: 'What To Bring', path: '/event-details/what-to-bring' },
  { name: 'Rules & Terms', path: '/event-details/rules-terms' },
];

const applicationLinks = [
  { name: 'Artists', path: '/applications/musicians' },
  { name: 'Volunteers', path: '/applications/volunteers' },
];

export default function Footer() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/newsletter`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      
      if (response.ok) {
        toast.success('Welcome to the forest! Check your inbox.');
        setEmail('');
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Something went wrong');
      }
    } catch {
      toast.error('Failed to subscribe. Please try again.');
    }
    setLoading(false);
  };

  return (
    <footer className="bg-ff-green" data-testid="footer">
      {/* Newsletter Section */}
      <div className="border-b border-ff-outline/20">
        <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 sm:py-16">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-display text-2xl sm:text-3xl text-ff-brown mb-2">
                Join the Forest
              </h3>
              <p className="text-ff-brown/60">
                Get lineup announcements, ticket releases, and festival news.
              </p>
            </div>
            <form onSubmit={handleNewsletterSubmit} className="flex gap-3" data-testid="newsletter-form">
              <input
                type="email"
                placeholder="Your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="flex-1 bg-white/50 border-2 border-ff-outline/30 text-ff-brown rounded-full px-5 py-3 focus:border-ff-yellow outline-none transition-colors placeholder:text-ff-brown/40"
                required
                data-testid="newsletter-email-input"
              />
              <button
                type="submit"
                disabled={loading}
                className="bg-ff-yellow text-ff-brown px-6 py-3 rounded-full font-semibold hover:brightness-105 transition-all flex items-center gap-2"
                data-testid="newsletter-submit-btn"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 sm:py-16">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-12">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="inline-block mb-4">
              <span className="font-supertaste text-xl text-ff-yellow drop-shadow-[2px_2px_0px_#A63902]">
                FREQUENCY FOREST
              </span>
            </Link>
            <p className="text-ff-brown/60 text-sm mb-4">
              25–27 September 2026<br />
              Maitland Showgrounds<br />
              NSW, Australia
            </p>
            <div className="flex gap-4">
              <a href={SOCIAL_LINKS.facebook} target="_blank" rel="noopener noreferrer" className="text-ff-brown/40 hover:text-ff-brown transition-colors" aria-label="Facebook" data-testid="footer-facebook-link">
                <Facebook className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.instagram} target="_blank" rel="noopener noreferrer" className="text-ff-brown/40 hover:text-ff-brown transition-colors" aria-label="Instagram" data-testid="footer-instagram-link">
                <Instagram className="w-5 h-5" />
              </a>
              <a href={SOCIAL_LINKS.tiktok} target="_blank" rel="noopener noreferrer" className="text-ff-brown/40 hover:text-ff-brown transition-colors" aria-label="TikTok" data-testid="footer-tiktok-link">
                <TikTokIcon className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-ff-brown text-sm font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-ff-brown/60 hover:text-ff-brown transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Event Info */}
          <div>
            <h4 className="text-ff-brown text-sm font-semibold mb-4">Event Info</h4>
            <ul className="space-y-2">
              {eventLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-ff-brown/60 hover:text-ff-brown transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Applications */}
          <div>
            <h4 className="text-ff-brown text-sm font-semibold mb-4">Get Involved</h4>
            <ul className="space-y-2">
              {applicationLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-sm text-ff-brown/60 hover:text-ff-brown transition-colors">
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Acknowledgement of Country */}
        <div className="border-t border-ff-outline/20 pt-8 mb-8">
          <div className="bg-white/30 p-6 rounded-2xl">
            <h4 className="text-ff-brown text-xs uppercase tracking-wider mb-3 font-semibold">
              Acknowledgement of Country
            </h4>
            <p className="text-sm text-ff-brown/60 leading-relaxed">
              Frequency Forest acknowledges the Traditional Custodians of the land on which we gather, 
              and pays respect to their Elders past and present. We recognise the ongoing connection 
              of Aboriginal and Torres Strait Islander peoples to Country and commit to celebrating 
              and learning from the world's oldest living culture.
            </p>
          </div>
        </div>

        {/* Copyright */}
        <div className="border-t border-ff-outline/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-ff-brown/40">
            © 2026 Frequency Forest. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/event-details/rules-terms" className="text-sm text-ff-brown/40 hover:text-ff-brown/60 transition-colors">
              Terms & Conditions
            </Link>
            <a href="#" className="text-sm text-ff-brown/40 hover:text-ff-brown/60 transition-colors">
              Privacy Policy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
